import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect, useRef } from "react";
import { Zap, Target, Trophy, Timer, Crosshair, Play, RotateCcw } from "lucide-react";

// Custom CSS for electric effects
const customStyles = `
  .text-glow {
    text-shadow: 0 0 10px rgba(56, 189, 248, 0.5), 0 0 20px rgba(56, 189, 248, 0.3);
  }
  .target-glow {
    box-shadow: 0 0 15px rgba(234, 179, 8, 0.6), inset 0 0 10px rgba(234, 179, 8, 0.4);
  }
  .grid-bg {
    background-size: 50px 50px;
    background-image: linear-gradient(to right, rgba(255, 255, 255, 0.05) 1px, transparent 1px),
                      linear-gradient(to bottom, rgba(255, 255, 255, 0.05) 1px, transparent 1px);
  }
`;

const GamePlay = () => {
  const [score, setScore] = useState(0);
  const [targets, setTargets] = useState<{ id: number; x: number; y: number }[]>([]);
  
  // Game States
  const [isCountingDown, setIsCountingDown] = useState(false);
  const [countdownValue, setCountdownValue] = useState(3);
  const [gameActive, setGameActive] = useState(false);
  const [gameMode, setGameMode] = useState<'practice' | 'timed'>('practice');
  const [timeLeft, setTimeLeft] = useState(30);
  const [gameEnded, setGameEnded] = useState(false);

  // Audio refs could go here for sound effects

  const initiateGameSequence = (mode: 'practice' | 'timed' = 'practice') => {
    setGameMode(mode);
    setScore(0);
    setGameEnded(false);
    setTargets([]);
    setTimeLeft(mode === 'timed' ? 30 : 0);
    
    // Start Countdown Sequence
    setCountdownValue(3);
    setIsCountingDown(true);
  };

  const hitTarget = (id: number) => {
    setScore(prev => prev + 10);
    setTargets(prev => prev.filter(t => t.id !== id));
  };

  // Countdown Logic
  useEffect(() => {
    if (isCountingDown) {
      if (countdownValue > 0) {
        const timer = setTimeout(() => setCountdownValue(prev => prev - 1), 1000);
        return () => clearTimeout(timer);
      } else {
        // Countdown finished, start actual game
        setIsCountingDown(false);
        setGameActive(true);
      }
    }
  }, [isCountingDown, countdownValue]);

  // Game Loop (Targets & Timer)
  useEffect(() => {
    if (!gameActive) return;

    // Spawn Targets
    const targetInterval = setInterval(() => {
      const newTarget = {
        id: Date.now(),
        // Keep within 10%-90% bounds to avoid edge clipping
        x: Math.random() * 80 + 10, 
        y: Math.random() * 70 + 15, 
      };
      // Limit max targets on screen to prevent clutter
      setTargets(prev => [...prev.slice(-5), newTarget]); 
    }, gameMode === 'timed' ? 600 : 800); // Faster spawns in timed mode

    // Game Timer (only for timed mode)
    const timerInterval = gameMode === 'timed' ? setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          setGameActive(false);
          setGameEnded(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000) : null;

    return () => {
      clearInterval(targetInterval);
      if (timerInterval) clearInterval(timerInterval);
    };
  }, [gameActive, gameMode]);

  return (
    <main className="min-h-screen bg-slate-950 text-white font-sans overflow-hidden relative selection:bg-cyan-500 selection:text-black">
      <style>{customStyles}</style>
      
      {/* Background Ambience */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-slate-900 via-slate-950 to-black pointer-events-none" />
      <div className="absolute inset-0 grid-bg opacity-20 pointer-events-none" />
      
      <div className="container mx-auto px-4 py-12 relative z-10 max-w-5xl">
        
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="inline-flex items-center gap-2 mb-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/30 backdrop-blur-md">
            <Crosshair className="w-4 h-4 text-cyan-400" />
            <span className="text-xs font-bold tracking-widest uppercase text-cyan-300">Target Practice v2.0</span>
          </div>
          <h1 className="text-5xl font-black mb-2 tracking-tight">
            THUNDER <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-500 text-glow">ARENA</span>
          </h1>
          <p className="text-slate-400 text-lg">Reflex Training Module</p>
        </motion.div>

        {/* Stats Bar */}
        <div className="flex justify-center gap-6 mb-8">
           <div className="bg-slate-900/80 backdrop-blur border border-slate-700 px-6 py-3 rounded-xl flex items-center gap-4 min-w-[150px]">
              <div className="p-2 bg-yellow-500/20 rounded-lg">
                <Trophy className="w-6 h-6 text-yellow-500" />
              </div>
              <div>
                <div className="text-xs text-slate-400 uppercase font-bold">Score</div>
                <div className="text-2xl font-mono font-bold text-white">{score}</div>
              </div>
           </div>

           {gameMode === 'timed' && (
             <div className={`bg-slate-900/80 backdrop-blur border px-6 py-3 rounded-xl flex items-center gap-4 min-w-[150px] ${timeLeft <= 5 ? 'border-red-500 animate-pulse' : 'border-slate-700'}`}>
                <div className="p-2 bg-cyan-500/20 rounded-lg">
                  <Timer className={`w-6 h-6 ${timeLeft <= 5 ? 'text-red-500' : 'text-cyan-500'}`} />
                </div>
                <div>
                  <div className="text-xs text-slate-400 uppercase font-bold">Time</div>
                  <div className={`text-2xl font-mono font-bold ${timeLeft <= 5 ? 'text-red-500' : 'text-white'}`}>
                    00:{timeLeft < 10 ? `0${timeLeft}` : timeLeft}
                  </div>
                </div>
             </div>
           )}
        </div>

        {/* Countdown Overlay */}
        <AnimatePresence>
          {isCountingDown && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 z-50 flex items-center justify-center pointer-events-none"
            >
              <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
              <motion.div 
                key={countdownValue}
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 2, opacity: 0 }}
                transition={{ duration: 0.5 }}
                className="relative z-10"
              >
                <span className="text-9xl font-black italic text-transparent bg-clip-text bg-gradient-to-br from-cyan-400 to-purple-500 text-glow">
                  {countdownValue === 0 ? "GO!" : countdownValue}
                </span>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Game Area */}
        <div className="relative w-full aspect-[16/9] md:aspect-[2/1] bg-slate-900/50 rounded-2xl border border-slate-700 overflow-hidden shadow-2xl backdrop-blur-sm mx-auto group cursor-crosshair">
          
          {/* Static decoration inside game area */}
          <div className="absolute inset-0 flex items-center justify-center opacity-5 pointer-events-none">
             <Crosshair className="w-64 h-64 text-white" />
          </div>

          {!gameActive && !isCountingDown && !gameEnded && (
             <div className="absolute inset-0 flex flex-col items-center justify-center z-20 bg-slate-950/40 backdrop-blur-sm">
                <div className="flex gap-4">
                  <button 
                    onClick={() => initiateGameSequence('practice')}
                    className="flex items-center gap-2 px-8 py-4 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-xl text-white font-bold transition-all hover:scale-105"
                  >
                    <Play className="w-5 h-5" />
                    Practice Mode
                  </button>
                  <button 
                    onClick={() => initiateGameSequence('timed')}
                    className="flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 rounded-xl text-white font-bold shadow-lg shadow-cyan-500/20 transition-all hover:scale-105"
                  >
                    <Zap className="w-5 h-5" />
                    30s Challenge
                  </button>
                </div>
             </div>
          )}

          {/* Game Over Screen */}
          {gameEnded && (
             <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="absolute inset-0 z-30 flex flex-col items-center justify-center bg-black/80 backdrop-blur-md"
             >
                <h2 className="text-4xl font-bold text-white mb-2">Session Complete</h2>
                <div className="text-6xl font-black text-yellow-400 mb-8 text-glow">{score} PTS</div>
                <div className="flex gap-4">
                   <button 
                      onClick={() => initiateGameSequence('timed')}
                      className="flex items-center gap-2 px-6 py-3 bg-white text-black rounded-lg font-bold hover:bg-gray-200 transition-colors"
                   >
                      <RotateCcw className="w-5 h-5" />
                      Try Again
                   </button>
                   <button 
                      onClick={() => setGameEnded(false)}
                      className="px-6 py-3 border border-white/20 text-white rounded-lg hover:bg-white/10 transition-colors"
                   >
                      Menu
                   </button>
                </div>
             </motion.div>
          )}

          {/* Active Targets */}
          <AnimatePresence>
            {gameActive && targets.map(target => (
              <motion.button
                key={target.id}
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0, opacity: 0 }}
                transition={{ type: "spring", stiffness: 300, damping: 20 }}
                className="absolute w-16 h-16 -ml-8 -mt-8 flex items-center justify-center focus:outline-none"
                style={{ left: `${target.x}%`, top: `${target.y}%` }}
                onClick={(e) => {
                  // Prevent click propagation if needed, though button usually handles it
                  e.stopPropagation();
                  hitTarget(target.id);
                }}
              >
                <div className="relative w-12 h-12 bg-gradient-to-br from-yellow-400 to-orange-600 rounded-full flex items-center justify-center target-glow hover:scale-110 active:scale-95 transition-transform">
                   <div className="absolute inset-0 rounded-full border-2 border-white/50 animate-ping" />
                   <Target className="w-6 h-6 text-white drop-shadow-md" />
                </div>
              </motion.button>
            ))}
          </AnimatePresence>

          {/* Stop Button (Small, in corner) */}
          {gameActive && (
             <button 
                onClick={() => setGameActive(false)}
                className="absolute top-4 right-4 p-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500 hover:text-white transition-colors border border-red-500/50 text-xs font-bold uppercase tracking-wider"
             >
                Abort
             </button>
          )}
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-8 text-center text-slate-500 text-sm font-mono"
        >
          {gameActive ? "/// SYSTEM ENGAGED: DESTROY TARGETS ///" : "/// SYSTEM IDLE: AWAITING INPUT ///"}
        </motion.p>

      </div>
    </main>
  );
};

export default GamePlay;